import { useQuery } from "@tanstack/react-query";
import { Badge, Card, Page, Select, Table, type TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import { TraceProgress, jobCounts } from "../components/TraceProgress.tsx";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDate, fmtNum, isJobActive, jobStatusTone } from "../lib/format.ts";
import type { PartnerRow, Rows, TraceJob } from "../lib/types.ts";

const STATUS_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: "queued", label: "Queued" },
	{ value: "running", label: "Running" },
	{ value: "done", label: "Done" },
	{ value: "error", label: "Error" },
	{ value: "cancelled", label: "Cancelled" },
];

export function Traces() {
	const [status, setStatus] = useState("");
	const [partnerId, setPartnerId] = useState("");

	const partners = useQuery({
		queryKey: ["partners", "", ""],
		queryFn: () => api.get<Rows<PartnerRow>>("/api/partners?q=&status="),
	});
	const list = useQuery({
		queryKey: ["traces", status, partnerId],
		queryFn: () =>
			api.get<Rows<TraceJob>>(
				`/api/traces?status=${encodeURIComponent(status)}&partnerId=${encodeURIComponent(partnerId)}`,
			),
		refetchInterval: (q) =>
			q.state.data?.rows.some((t) => isJobActive(t.status)) ? 5_000 : 30_000,
	});

	const columns: TableColumn<TraceJob>[] = [
		{
			id: "id",
			header: "Job",
			accessor: (r) => (
				<div>
					<Link to={`/traces/${r.id}`} className="text-primary hover:underline font-mono text-xs">
						{r.id.slice(0, 8)}…
					</Link>
					<div className="text-[11px] text-muted">{r.kind}</div>
				</div>
			),
		},
		{
			id: "status",
			header: "Status",
			accessor: (r) => <Badge variant={jobStatusTone(r.status)}>{r.status}</Badge>,
		},
		{
			id: "partner",
			header: "Partner",
			accessor: (r) =>
				r.partnerId ? (
					<Link to={`/partners/${r.partnerId}`} className="text-primary hover:underline">
						{r.partnerName ?? r.partnerId.slice(0, 8)}
					</Link>
				) : (
					<span className="text-muted">seeds</span>
				),
		},
		{
			id: "params",
			header: "Params",
			accessor: (r) => (
				<span className="text-xs text-muted">
					d{r.params?.depth} · f{r.params?.fanout} · {r.params?.direction} · ≥${r.params?.minUsd}
				</span>
			),
		},
		{
			id: "progress",
			header: "Progress",
			accessor: (r) => <TraceProgress counts={jobCounts(r)} compact />,
		},
		{
			id: "nodes",
			header: "Nodes",
			accessor: (r) => (
				<span title={`${fmtNum(r.nodesEnqueued)} enqueued`}>
					{fmtNum(r.nodesVisited)}
					{r.budgetHit && (
						<Badge variant="warning" size="sm" className="ml-1">
							budget
						</Badge>
					)}
				</span>
			),
		},
		{ id: "calls", header: "Calls", accessor: (r) => fmtNum(r.providerCalls) },
		{
			id: "created",
			header: "Created",
			accessor: (r) => <span title={fmtDate(r.createdAt)}>{fmtAgo(r.createdAt)}</span>,
		},
		{
			id: "finished",
			header: "Finished",
			accessor: (r) =>
				r.finishedAt ? fmtAgo(r.finishedAt) : r.lastTickAt ? `tick ${fmtAgo(r.lastTickAt)}` : "—",
		},
	];

	return (
		<Page
			title="Traces"
			description="Graph expansion jobs (on wallet add, deep traces, refetches)."
		>
			<div className="mb-3 flex flex-wrap gap-2">
				<div className="w-44">
					<Select
						value={status}
						onChange={(e) => setStatus(e.target.value)}
						options={STATUS_OPTIONS}
					/>
				</div>
				<div className="w-56">
					<Select
						value={partnerId}
						onChange={(e) => setPartnerId(e.target.value)}
						options={[
							{ value: "", label: "All partners" },
							...(partners.data?.rows ?? []).map((p) => ({ value: p.id, label: p.name })),
						]}
					/>
				</div>
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No trace jobs."
					/>
				</div>
			</Card>
		</Page>
	);
}
